import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useSplashComplete } from "@/components/feature/SplashContext";

const slides = [
  {
    image:
      "https://storage.helloreaddy.io/project_files/3947077b-25d2-4d94-b656-7a710d99b9c9/02398040-a1d8-453b-8ef7-9b70ddbc1965_compressed_IMG_0940.webp",
    alt: "森祐作",
    objectPosition: "object-[72%_20%]" as const,
    subtitle: "OLカンパニーとは？",
    title: (
      <>
        「LOCAL FIRST」を社訓に掲げる株式会社OLカンパニー。
        <br className="hidden md:block" />
        OL＝OITA LOCALの頭文字です。
        <br className="hidden md:block" />
        大分県のローカルタレント・森祐作のマネジメントを目的とする個人事務所として設立され、今では大分の特産品かぼすの生産、販売も手掛けております。
        <br className="hidden md:block" />
        私たちは、大分の魅力を世界へ届けるために、いままでにない価値を創造しつづけます。
      </>
    ),
    subtitleClass: "text-xl md:text-2xl lg:text-3xl",
    titleClass: "text-sm sm:text-base md:text-lg lg:text-xl leading-[1.9] md:leading-[2.0]",
  },
  {
    image:
      "https://static.readdy.ai/image/8da10c853ee44f0b201c0b84a1169777/29f672ed59c1287ae0bed646066ce370.png",
    alt: "OL Company Talk",
    objectPosition: "object-[85%_50%] md:object-center" as const,
    subtitle: "OLカンパニーとは？",
    title: (
      <>
        「LOCAL FIRST」を社訓に掲げる株式会社OLカンパニー。
        <br className="hidden md:block" />
        OL＝OITA LOCALの頭文字です。
        <br className="hidden md:block" />
        大分県のローカルタレント・森祐作のマネジメントを目的とする個人事務所として設立され、今では大分の特産品かぼすの生産、販売も手掛けております。
        <br className="hidden md:block" />
        私たちは、大分の魅力を世界へ届けるために、いままでにない価値を創造しつづけます。
      </>
    ),
    subtitleClass: "text-xl md:text-2xl lg:text-3xl",
    titleClass: "text-sm sm:text-base md:text-lg lg:text-xl leading-[1.9] md:leading-[2.0]",
  },
  {
    image:
      "https://static.readdy.ai/image/8da10c853ee44f0b201c0b84a1169777/a0d998d79bbf281065e4fac1929de548.png",
    alt: "大分特産 かぼすの生産・販売",
    objectPosition: "object-center" as const,
    subtitle: '"Kabosu Production"',
    title: (
      <>
        大分県の恵まれた自然環境で育まれた、
        <br className="hidden md:block" />
        特産品かぼすの生産・販売も手掛けています。
      </>
    ),
    subtitleClass: "text-[27px] md:text-4xl",
    titleClass: "text-base sm:text-lg md:text-2xl lg:text-3xl leading-[1.8] md:leading-[1.9]",
  },
  {
    image:
      "https://storage.helloreaddy.io/project_files/3947077b-25d2-4d94-b656-7a710d99b9c9/dd03a745-9b45-4484-9a44-7254c4fc4b04_compressed_1672941-.webp",
    alt: "乙御KABOS ブランド",
    objectPosition: "object-center" as const,
    objectFit: "object-contain",
    noText: true,
  },
  {
    image:
      "https://storage.helloreaddy.io/project_files/3947077b-25d2-4d94-b656-7a710d99b9c9/d326c3f4-7864-4ae7-8996-c05fbf391827_compressed_olchero.webp",
    alt: "乙御KABOS かぼすのグラノーラクッキー",
    objectPosition: "object-center" as const,
    objectFit: "object-contain",
    noText: true,
  },
];

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const { splashComplete } = useSplashComplete();

  const goTo = useCallback(
    (index: number) => {
      if (isTransitioning || index === current) return;
      setIsTransitioning(true);
      setCurrent(index);
      setTimeout(() => setIsTransitioning(false), 1600);
    },
    [current, isTransitioning]
  );

  const next = useCallback(() => {
    goTo((current + 1) % slides.length);
  }, [current, goTo]);

  useEffect(() => {
    if (!splashComplete) return;
    const delay = current === 0 ? 2700 : 2400;
    const timer = setInterval(next, delay);
    return () => clearInterval(timer);
  }, [splashComplete, current, next]);

  return (
    <section className="w-full h-[90vh] md:h-screen min-h-[600px] px-2 md:px-6 lg:px-8 pb-3 md:pb-6 lg:pb-8 pl-2 lg:pl-3 pt-2 md:pt-4">
      <div className="relative w-full h-full rounded-2xl overflow-hidden">
        {/* Background Slides */}
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-[2000ms] ease-in-out ${
              index === current ? "opacity-100 z-[1]" : "opacity-0 z-0"
            } ${slide.noText ? "bg-black" : ""}`}
          >
            <img
              src={slide.image}
              alt={slide.alt}
              className={`absolute inset-0 w-full h-full ${slide.objectFit || "object-cover"} ${slide.objectPosition}`}
            />
            {/* Overlay for text readability */}
            {!slide.noText && (
              <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30" />
            )}
          </div>
        ))}

        {/* Text Content — each slide has its own container to keep position identical */}
        {slides.map((slide, index) =>
          slide.noText ? null : (
          <div
            key={`text-${index}`}
            className={`absolute inset-0 z-10 flex flex-col items-center md:items-start justify-center px-6 md:px-14 lg:px-20 text-center md:text-left pt-[75%] md:pt-0 w-full md:max-w-4xl transition-opacity duration-1500 ${
              index === current ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          >
            {/* English subtitle */}
            <p
              className={`tracking-[0.3em] text-white mb-6 uppercase ${slide.subtitleClass || "text-[27px] md:text-4xl"}`}
              style={{ fontFamily: "'Noto Serif JP', serif", fontWeight: 400 }}
            >
              {slide.subtitle}
            </p>

            {/* Main title */}
            <h1
              className={`text-white mb-10 ${slide.titleClass || "text-base sm:text-lg md:text-2xl lg:text-3xl leading-[1.8] md:leading-[1.9]"}`}
              style={{ fontFamily: "'Noto Serif JP', serif", fontWeight: 500 }}
            >
              {slide.title}
            </h1>

            {/* ABOUT US button — shown on both slides */}
            <Link
              to="/about"
              className="hidden md:inline-flex items-center gap-3 border border-white/70 text-white text-xs md:text-sm tracking-[0.2em] px-10 py-4 hover:bg-white hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              ABOUT US
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        ))}

        {/* Slide indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goTo(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                index === current
                  ? "bg-white w-6"
                  : "bg-white/40 hover:bg-white/60"
              }`}
              aria-label={`スライド ${index + 1} へ`}
            />
          ))}
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-4 right-4 sm:bottom-20 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 flex flex-col items-center gap-2 animate-bounce z-20">
          <span className="text-[10px] tracking-widest text-white/40 uppercase">
            Scroll
          </span>
          <i className="ri-arrow-down-line text-white/40 text-sm" />
        </div>
      </div>
    </section>
  );
}